import { Button, Center, Skeleton, Stack, Table, Text, ThemeIcon } from '@mantine/core';
import { IconAlertTriangle, IconChevronRight, IconInbox } from '@tabler/icons-react';

import type { Operation } from '../api/types';
import { formatDateTime, formatMoney } from '../lib/format';
import { KIND_COLOR } from '../theme';
import { CategoryBadge } from './CategoryBadge';
import { KindBadge } from './KindBadge';

const COLUMNS = ['Когда', 'Название', 'Категория', 'Тип', 'Сумма', ''];
const AMOUNT_COLUMN = 4;
const CHEVRON_COLUMN = 5;
const SKELETON_ROWS = 5;

interface OperationsTableProps {
  operations: Operation[];
  loading: boolean;
  error: Error | null;
  onRetry: () => void;
  onSelect: (id: string) => void;
}

export function OperationsTable({
  operations,
  loading,
  error,
  onRetry,
  onSelect,
}: OperationsTableProps) {
  if (error) {
    return <ErrorState message={error.message} onRetry={onRetry} />;
  }

  if (loading) {
    return <TableSkeleton />;
  }

  if (operations.length === 0) {
    return <EmptyState />;
  }

  return (
    <Table.ScrollContainer minWidth={760}>
      <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
        <Table.Thead>
          <Table.Tr>
            {COLUMNS.map((column, index) => (
              <Table.Th
                key={column || 'chevron'}
                ta={index === AMOUNT_COLUMN ? 'right' : undefined}
                w={index === CHEVRON_COLUMN ? 40 : undefined}
              >
                <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                  {column}
                </Text>
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {operations.map((operation) => (
            <OperationRow key={operation.id} operation={operation} onSelect={onSelect} />
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

function OperationRow({
  operation,
  onSelect,
}: {
  operation: Operation;
  onSelect: (id: string) => void;
}) {
  return (
    <Table.Tr
      style={{ cursor: 'pointer' }}
      onClick={() => onSelect(operation.id)}
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          onSelect(operation.id);
        }
      }}
    >
      <Table.Td>
        <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
          {formatDateTime(operation.occurred_at)}
        </Text>
      </Table.Td>

      <Table.Td>
        <Text size="sm" lineClamp={1}>
          {operation.title}
        </Text>
      </Table.Td>

      <Table.Td>
        <CategoryBadge category={operation.category} />
      </Table.Td>

      <Table.Td>
        <KindBadge kind={operation.kind} />
      </Table.Td>

      <Table.Td ta="right">
        <Text
          size="sm"
          fw={600}
          c={KIND_COLOR[operation.kind]}
          style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
        >
          {formatMoney(operation.signed_amount, { withSign: true })}
        </Text>
      </Table.Td>

      <Table.Td>
        <IconChevronRight size={16} stroke={1.5} opacity={0.4} />
      </Table.Td>
    </Table.Tr>
  );
}

function TableSkeleton() {
  return (
    <Stack gap="sm" py="xs">
      {Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <Skeleton key={index} height={34} radius="sm" />
      ))}
    </Stack>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Center py={40}>
      <Stack gap="xs" align="center" maw={360}>
        <ThemeIcon variant="light" color="red" size="lg" radius="md">
          <IconAlertTriangle size={18} stroke={1.5} />
        </ThemeIcon>
        <Text size="sm" fw={600}>
          Не удалось загрузить операции
        </Text>
        <Text size="xs" c="dimmed" ta="center">
          {message}
        </Text>
        <Button size="xs" variant="default" mt={4} onClick={onRetry}>
          Повторить
        </Button>
      </Stack>
    </Center>
  );
}

function EmptyState() {
  return (
    <Center py={48}>
      <Stack gap="xs" align="center">
        <IconInbox size={28} stroke={1.5} opacity={0.5} />
        <Text size="sm" c="dimmed">
          Операций нет
        </Text>
      </Stack>
    </Center>
  );
}
